import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import useAuthLogin from "hooks/useAuthLogin";
import { yupResolver } from "@hookform/resolvers/yup";
import { Link } from "react-router-dom";
import { fetch as router } from "helpers/router";
import classNames from "classnames";
import { useTurnstile } from "components/TurnstileProvider";
import MFAInput from "../../../../components/MFAInput";
import Error from "../InputGroupError";
import validationSchema from "./validation";

const applyServerErrors = (errors, handler, map) => {
  errors.forEach((error) => handler(map[error.key], { message: error.message }));
};

const LoginForm = () => {
  const {
    login,
    location,
    errors: serverError,
    loading: isLoggingIn,
    submitError,
  } = useAuthLogin();
  const { captchaToken, refreshTurnstile } = useTurnstile();
  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm({
    resolver: yupResolver(validationSchema),
  });
  if (location !== null) {
    window.location.assign(location);
  }
  const captchaErrorText = "Captcha test failed";

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [requiresMfa, setRequiresMfa] = useState(false);
  const [invalidMfaCode, setInvalidMfaCode] = useState(false);

  const flagMfaError = (serverError) => {
    const invalidMessage = "Multifactor Authentication Code invalid.";
    const mfaMessages = ["Multifactor Authentication is required.", invalidMessage];
    let hasInvalidCode = false;
    for (const error of serverError) {
      if (error.key === "input" && mfaMessages.includes(error.message)) {
        setRequiresMfa(true);
        hasInvalidCode = error.message === invalidMessage;
      }
    }
    setInvalidMfaCode(hasInvalidCode);
  };

  const initialCaptureToken = useRef(null);

  // set initial token or check if captcha token has refreshed
  useEffect(() => {
    if (
      captchaToken.token
      && (initialCaptureToken.current === null
        || initialCaptureToken.current !== captchaToken.token
        || (captchaToken.token === "XXXX.DUMMY.TOKEN.XXXX" && process.env.NODE_ENV !== "production"))
    ) {
      setIsRefreshing(false);
      initialCaptureToken.current = captchaToken.token;
    }
  }, [captchaToken]);

  useEffect(() => {
    if (Object.keys(serverError).length === 0) {
      return;
    }
    applyServerErrors(serverError, setError, {
      "input.email": "email",
      "input.password": "password",
      input: "credentials",
    });
    flagMfaError(serverError);
    setIsRefreshing(true);
    refreshTurnstile();
  }, [serverError, setError, refreshTurnstile]);

  useEffect(() => {
    if (submitError && submitError !== captchaErrorText) {
      setIsRefreshing(true);
      refreshTurnstile();
    }
  }, [submitError, refreshTurnstile]);

  const onSubmit = (data) => {
    login(data.email, data.password, data.mfaCode, captchaToken.token);
  };

  const handleBack = () => {
    setRequiresMfa(null);
    setIsRefreshing(false);
    refreshTurnstile();
  };
  return (
    <>
      <h3 data-test="login-header" className="login-header">
        Log in
      </h3>
      <form onSubmit={handleSubmit(onSubmit)}>
        {!requiresMfa ? (
          <>
            <div className="form-group">
              <label htmlFor="login_email">Email</label>
              <input
                {...register("email")}
                className="form-control"
                placeholder="Enter email"
                id="login_email"
              />
              <Error name="email" errors={errors} />
            </div>

            <div className="form-group">
              <label htmlFor="login_password">
                Password
                {" "}
                <Link
                  to={router("auth.password.reset.index")}
                  style={{ fontSize: "14px", fontWeight: "normal" }}
                >
                  ( Forgot your password? )
                </Link>
              </label>

              <input
                {...register("password")}
                type="password"
                name="password"
                className="form-control"
                id="login_password"
                placeholder="Enter password"
              />
              <Error name="password" errors={errors} />
            </div>

            <div className="form-group credentials-error">
              <Error name="credentials" errors={errors} />
            </div>
          </>
        ) : (
          <div className="form-group">
            <MFAInput {...register("mfaCode")} disabled={isRefreshing} />
            {invalidMfaCode && <Error name="credentials" errors={errors} />}
          </div>
        )}
        <div
          className={classNames("form-group", { "--mfa-required ": requiresMfa })}
          style={{ display: "flex", justifyContent: "space-between" }}
        >
          <button
            id="login-submit"
            type="submit"
            disabled={isLoggingIn || submitError === captchaErrorText || isRefreshing}
            className={classNames("link-button --bold --primary --full", {
              "--loading": isLoggingIn,
              "--disabled": isLoggingIn || submitError === captchaErrorText,
            })}
            style={{ border: "0px solid #FFF" }}
          >
            Login
          </button>
          {requiresMfa && (
            <button
              id="login-back"
              type="button"
              onClick={handleBack}
              disabled={isLoggingIn || submitError === captchaErrorText}
              className={classNames("link-button --bold --secondary-outline", {
                "--loading": isLoggingIn,
                "--disabled": isLoggingIn || submitError === captchaErrorText,
              })}
            >
              Cancel
            </button>
          )}
        </div>

        {submitError && (
          <div className="login-form__error">
            {submitError}
            {" "}
            Please reload page and try again
          </div>
        )}
      </form>
      {!requiresMfa && (
        <div className="content-footer">
          <p>
            Don't have an account yet?
            {" "}
            <Link to={router("signup")}>Sign up</Link>
          </p>
        </div>
      )}
    </>
  );
};

export default LoginForm;
